import {
  combineReducers,
  configureStore,
  type AnyAction,
} from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import storageSession from 'redux-persist/lib/storage/session';
import { authApi } from '../services/AuthService';
import authSessionReducer from './reducers/AuthSessionSlice';
import authReducer, { logOut } from './reducers/AuthSlice';
import { campaignSlice } from './reducers/CampaignSlice';
import { clinicSlice } from './reducers/ClinicSlice';
import { medVoxEventSlice } from './reducers/MedVoxEventSlice';
import { medVoxTaskSlice } from './reducers/MedVoxTaskSlice';
import { questionnaireSlice } from './reducers/QuestionnaireSlice';
import { roboEventSlice } from './reducers/RoboEventSlice';
import { roboTaskSlice } from './reducers/RoboTaskSlice';
import { settingsSlice } from './reducers/SettingsSlice';
import { statisticsSlice } from './reducers/StatisticSlice';
import { sipAccountSlice } from './reducers/SIPAccountSlice';
import { outgoingCallsSlice } from './reducers/OutgoingCallsSlice';
import { incomingCallsSlice } from './reducers/IncomingCallsSlice';
import { specializationDoctorSlice } from './reducers/SpecializationDoctorSlice';
import { surnameDoctorSlice } from './reducers/surnameDoctor';
import { blockNumbersSlice } from './reducers/BlockNumbersSlice';
import { outgoingStatisticsSlice } from './reducers/OutgoingStatisticsSlice';
import { incomingStatisticsSlice } from './reducers/IncomingStatisticsSlice';
import { dispensarizationStatisticsSlice } from './reducers/DispensarizationStatisticsSlice';
import { reportSlice } from './reducers/ReportSlice';
import { incomingReportSlice } from './reducers/IncomingReportSlice';
import { dispensarizationsReportSlice } from './reducers/DispensarizationsReportSlice';
import { blacklistSlice } from './reducers/BlacklistSlice';
import { dispensarizationsSlice } from './reducers/DispensarizationsSlice';
import { dispensarizationBatchSlice } from './reducers/DispensarizationBatchesSlice';
import { outgoingCallsUploadSlice } from './reducers/OutgoingCallsUploadSlice';
import { clinicSettingsSlice } from './reducers/ClinicSettingsSlice';
import { clinicSettingsDetailSlice } from './reducers/ClinicSettingsDetailSlice';
import { specializationDoctorCatalogSlice } from './reducers/SpecializationDoctorCatalogSlice';
import { callPoolSlice } from './reducers/CallPoolSlice';
import { roleSlice } from './reducers/RoleSlice';
import { usersSlice } from './reducers/UsersSlice';
import { taskFormSlice } from './reducers/TaskFormSlice';

const authPersistConfig = {
  key: 'auth',
  storage: storage,
};

const sessionPersistConfig = {
  key: 'authSession',
  storage: storageSession,
};

const appReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  authSession: persistReducer(sessionPersistConfig, authSessionReducer),
  settings: settingsSlice.reducer,
  sipAccounts: sipAccountSlice.reducer,
  questionnaires: questionnaireSlice.reducer,
  campaign: campaignSlice.reducer,
  statistics: statisticsSlice.reducer,
  roboTask: roboTaskSlice.reducer,
  medVoxTask: medVoxTaskSlice.reducer,
  clinic: clinicSlice.reducer,
  specializations: specializationDoctorSlice.reducer,
  role: roleSlice.reducer,
  surnameDoctor: surnameDoctorSlice.reducer,
  roboEvent: roboEventSlice.reducer,
  medVoxEvent: medVoxEventSlice.reducer,
  outgoingCalls: outgoingCallsSlice.reducer,
  dispensarizationBatch: dispensarizationBatchSlice.reducer,
  outgoingCallsUpload: outgoingCallsUploadSlice.reducer,
  dispensarizations: dispensarizationsSlice.reducer,
  incomingCalls: incomingCallsSlice.reducer,
  outgoingStatistics: outgoingStatisticsSlice.reducer,
  incomingStatistics: incomingStatisticsSlice.reducer,
  dispensarizationStatistics: dispensarizationStatisticsSlice.reducer,
  blockNumbers: blockNumbersSlice.reducer,
  report: reportSlice.reducer,
  incomingReport: incomingReportSlice.reducer,
  dispensarizationsReport: dispensarizationsReportSlice.reducer,
  blacklist: blacklistSlice.reducer,
  clinicSettings: clinicSettingsSlice.reducer,
  clinicSettingsDetail: clinicSettingsDetailSlice.reducer,
  specializationDoctorCatalog: specializationDoctorCatalogSlice.reducer,
  callPools: callPoolSlice.reducer,
  users: usersSlice.reducer,
  taskForm: taskFormSlice.reducer,
  [authApi.reducerPath]: authApi.reducer,
});

export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
) => {
  if (action.type === logOut.type) {
    if (!state) {
      return appReducer(undefined, action);
    }

    return appReducer(
      {
        auth: state.auth,
        authSession: state.authSession,
      } as ReturnType<typeof appReducer>,
      action,
    );
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(authApi.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor = persistStore(store);
